import React, { useState, useEffect } from 'react';
import '../styles/WorkerManagement.css';
import { getCompanyMonthlyAttendance, editWorkHours, updateUserWage, updateEmployeeMonthlyNote, getCompanyMonthlyNotes, downloadCompanyMonthlyExcel } from '../utils/api';

const WorkerManagement = () => {
  // 편집 모드 상태 관리
  const [isEditing, setIsEditing] = useState(false);
  // 데이터 로딩 상태
  const [loading, setLoading] = useState(true);
  // 오류 상태
  const [error, setError] = useState(null);
  // 현재 선택된 년도와 월
  const [selectedDate, setSelectedDate] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  
  // API에서 가져온 데이터 저장
  const [attendanceData, setAttendanceData] = useState(null);
  // 근로자 데이터 (UI용)
  const [workersData, setWorkersData] = useState({
    hourlyWages: [],
    monthlyNotes: [],
    dailyHours: {},
    overtimeHours: {},
    attendanceIds: {},
    modifiedRecords: {}
  });

  // 변경된 데이터 추적을 위한 state 추가
  const [changedData, setChangedData] = useState({});
  // 변경된 시급 추적을 위한 state 추가
  const [changedWages, setChangedWages] = useState({});
  // 변경된 월별 특이사항 추적을 위한 state 추가
  const [changedNotes, setChangedNotes] = useState({});

  // 초기 데이터 로드
  useEffect(() => {
    fetchAttendanceData();
  }, [selectedDate]);

  // API에서 데이터 가져오기
  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      
      // 로컬 스토리지에서 회사 코드 가져오기
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const companyCode = userInfo.middleCompanyCode;
      
      if (!companyCode) {
        throw new Error('회사 코드를 찾을 수 없습니다.');
      }
      
      console.log('API 요청 파라미터:', {
        companyCode,
        year: selectedDate.year,
        month: selectedDate.month
      });
      
      // 출퇴근 데이터 API 호출
      const response = await getCompanyMonthlyAttendance(
        companyCode, 
        selectedDate.year, 
        selectedDate.month
      );
      
      console.log('출퇴근 API 응답:', response);
      
      if (response.status === 'success' && response.data) {
        // 월별 특이사항 데이터 가져오기
        try {
          const notesResponse = await getCompanyMonthlyNotes(
            companyCode,
            selectedDate.year,
            selectedDate.month
          );
          
          console.log('특이사항 API 응답:', notesResponse);
          
          // 출퇴근 데이터와 월별 특이사항 함께 처리
          processAttendanceData(response.data, notesResponse);
        } catch (notesError) {
          console.error('특이사항 조회 오류:', notesError);
          // 특이사항 로드 실패해도 출퇴근 데이터는 처리
          processAttendanceData(response.data, []);
        }
      } else {
        throw new Error(response.message || '데이터를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('출퇴근 데이터 로드 오류:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // API 데이터를 UI 형식으로 변환
  const processAttendanceData = (data, notesData = []) => {
    // 새 API 응답 데이터 구조 확인
    if (!data.attendances || !data.employee_summaries) {
      console.error('데이터 형식이 올바르지 않습니다:', data);
      return;
    }
    
    const { attendances, employee_summaries } = data;
    
    // 월별 특이사항을 사용자 이메일별로 매핑
    const notesMap = {};
    notesData.forEach(note => {
      notesMap[note.user_email] = note.note;
    });
    
    // 1. 직원 정보 구성 (employee_summaries 사용)
    const uniqueUsers = employee_summaries.map(summary => {
      return {
        id: summary.user_email,
        name: summary.user_name || '이름 없음',
        email: summary.user_email,
        hourly_wage: summary.hourly_wage || 0,
        monthly_note: notesMap[summary.user_email] || "", // 특이사항 있으면 사용, 없으면 빈 문자열
        notice_confirmed: true, // 기본값
        
        // 요약 정보 - 모든 필드에 기본값 설정
        total_hours: summary.total_work_hours || 0,
        total_overtime: summary.total_overtime_hours || 0,
        regular_pay: summary.regular_pay || 0,
        special_pay: summary.special_pay || 0,  // 특별근무수당으로 변경
        holiday_pay: summary.weekly_holiday_pay || 0,
        social_insurance: summary.social_insurance_amount || 0, // 사회보험료 추가
        total_pay: summary.total_pay || 0,
        work_days: summary.work_days || 0
      };
    });
    
    // 2. 날짜별 데이터 구성
    const dailyRecords = {};
    const attendanceIds = {}; // attendance_id 저장
    
    // 날짜 포맷 함수 - YY/MM/DD (요일) 형식으로 변환
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      
      // 연도의 마지막 두 자리만 추출
      const year = date.getFullYear().toString().slice(2);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      
      // 요일 배열
      const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
      const weekday = weekdays[date.getDay()];
      
      return `${year}/${month}/${day} (${weekday})`;
    };
    
    // 모든 출근 기록을 날짜별로 그룹화
    attendances.forEach(record => {
      const date = record.date;
      const formattedDate = formatDate(date);
      
      if (!dailyRecords[formattedDate]) {
        dailyRecords[formattedDate] = [];
        attendanceIds[formattedDate] = [];
      }
      
      // 수정된 근무 시간이 있으면 그 값을 사용, 없으면 원래 work_hours 사용
      const workHours = record.approved_work_hours !== undefined ? 
        record.approved_work_hours : (record.work_hours !== null ? record.work_hours : 0);
        
      // 수정된 특근 시간이 있으면 그 값을 사용, 없으면 0으로 설정
      const overtimeHours = record.approved_overtime_hours !== undefined ? 
        record.approved_overtime_hours : (record.overtime_hours !== null ? record.overtime_hours : 0);
      
      dailyRecords[formattedDate].push({
        worker_id: record.user_email,
        work_hours: workHours,
        overtime_hours: overtimeHours,
        overtime_type: record.overtime_type,
        attendance_id: record.id,
        has_been_modified: record.has_been_modified || false
      });
      
      // 사용자별 attendance_id 저장
      attendanceIds[formattedDate].push({
        worker_id: record.user_email,
        attendance_id: record.id
      });
    });
    
    // 3. UI용 데이터 구성
    const hourlyWages = uniqueUsers.map(user => {
      // null, undefined 체크 추가
      const wage = user.hourly_wage !== undefined && user.hourly_wage !== null ? user.hourly_wage : 0;
      return wage.toLocaleString();
    });
    const monthlyNotes = uniqueUsers.map(user => user.monthly_note || "");
    
    // 4. 일별 근무시간 및 특근시간 구성
    const dailyHours = {};
    const overtimeHours = {};
    const recordedAttendanceIds = {}; // 최종 출력될 근무시간에 해당하는 attendance_id
    const modifiedRecords = {}; // 수정된 기록 저장
    
    Object.keys(dailyRecords).forEach(date => {
      dailyHours[date] = [];
      overtimeHours[date] = [];
      recordedAttendanceIds[date] = [];
      modifiedRecords[date] = [];
      
      uniqueUsers.forEach(user => {
        const record = dailyRecords[date].find(rec => rec.worker_id === user.id);
        
        if (record) {
          // work_hours 안전하게 처리
          const workHoursValue = record.work_hours !== undefined && record.work_hours !== null ? 
                                 record.work_hours : 0;
          dailyHours[date].push(workHoursValue.toString());
          
          // overtime_hours 안전하게 처리
          const overtimeValue = record.overtime_hours !== undefined && record.overtime_hours !== null ? 
                                record.overtime_hours : 0;
          overtimeHours[date].push(overtimeValue.toString());
          
          recordedAttendanceIds[date].push(record.attendance_id);
          modifiedRecords[date].push(record.has_been_modified || false);
        } else {
          // 해당 날짜에 기록이 없는 경우
          dailyHours[date].push('0');
          overtimeHours[date].push('0');
          recordedAttendanceIds[date].push(null);
          modifiedRecords[date].push(false);
        }
      });
    });
    
    // 5. API 응답 데이터를 UI에 맞게 변환하여 저장
    setAttendanceData({
      workers: uniqueUsers
    });
    
    // 데이터 설정
    setWorkersData({
      hourlyWages,
      monthlyNotes,
      dailyHours,
      overtimeHours,
      attendanceIds: recordedAttendanceIds,
      modifiedRecords
    });
  };

  // 이전 달로 이동
  const goToPrevMonth = () => {
    setSelectedDate(prev => {
      const newMonth = prev.month - 1;
      if (newMonth < 1) {
        return { year: prev.year - 1, month: 12 };
      }
      return { ...prev, month: newMonth };
    });
  };

  // 다음 달로 이동
  const goToNextMonth = () => {
    setSelectedDate(prev => {
      const newMonth = prev.month + 1;
      if (newMonth > 12) {
        return { year: prev.year + 1, month: 1 };
      }
      return { ...prev, month: newMonth };
    });
  };

  // 편집 모드 토글 함수
  const toggleEditMode = async () => {
    if (isEditing) {
      // 수정 모드에서 보기 모드로 변경 시 API 호출
      try {
        setLoading(true);
        
        // 로컬 스토리지에서 회사 코드 가져오기
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode;
        
        // 1. 변경된 근무시간이 있으면 API 호출
        const changedItems = Object.keys(changedData);
        if (changedItems.length > 0) {
          for (const attendanceId of changedItems) {
            const item = changedData[attendanceId];
            await editWorkHours(
              attendanceId,
              item.work_hours,
              item.overtime_hours
            );
          }
        }
        
        // 2. 변경된 시급이 있으면 API 호출
        const changedWageUsers = Object.keys(changedWages);
        if (changedWageUsers.length > 0) {
          for (const userEmail of changedWageUsers) {
            await updateUserWage(userEmail, changedWages[userEmail]);
          }
        }
        
        // 3. 변경된 월별 특이사항이 있으면 API 호출
        const changedNoteUsers = Object.keys(changedNotes);
        if (changedNoteUsers.length > 0) {
          for (const userEmail of changedNoteUsers) {
            await updateEmployeeMonthlyNote(
              companyCode,
              userEmail,
              selectedDate.year,
              selectedDate.month,
              changedNotes[userEmail]
            );
          }
        }
        
        // 데이터 다시 로드
        await fetchAttendanceData();
        
        // 변경 데이터 초기화
        setChangedData({});
        setChangedWages({});
        setChangedNotes({});
        
      } catch (error) {
        console.error('데이터 업데이트 오류:', error);
        alert(`데이터 업데이트 중 오류가 발생했습니다: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
    
    // 편집 모드 토글
    setIsEditing(!isEditing);
  };

  // 시급 변경 핸들러
  const handleHourlyWageChange = (index, value) => {
    const newHourlyWages = [...workersData.hourlyWages];
    newHourlyWages[index] = value;
    setWorkersData({...workersData, hourlyWages: newHourlyWages});
    
    // 변경된 시급 추적
    if (attendanceData && attendanceData.workers && attendanceData.workers[index]) {
      const userEmail = attendanceData.workers[index].id;
      setChangedWages(prev => ({
        ...prev,
        [userEmail]: value
      }));
    }
  };

  // 월별 특이사항 변경 핸들러
  const handleMonthlyNotesChange = (index, value) => {
    const newMonthlyNotes = [...workersData.monthlyNotes];
    newMonthlyNotes[index] = value;
    setWorkersData({...workersData, monthlyNotes: newMonthlyNotes});
    
    // 변경된 월별 특이사항 추적
    if (attendanceData && attendanceData.workers && attendanceData.workers[index]) {
      const userEmail = attendanceData.workers[index].id;
      setChangedNotes(prev => ({
        ...prev,
        [userEmail]: value
      }));
    }
  };

  // 일별 근무시간 변경 핸들러
  const handleDailyHoursChange = (date, index, value) => {
    const newDailyHours = {...workersData.dailyHours};
    newDailyHours[date][index] = value;
    setWorkersData({...workersData, dailyHours: newDailyHours});
    
    // 변경된 데이터 추적
    const attendanceId = workersData.attendanceIds[date][index];
    if (attendanceId) {
      setChangedData(prev => ({
        ...prev,
        [attendanceId]: {
          ...prev[attendanceId],
          work_hours: value,
          overtime_hours: workersData.overtimeHours[date][index]
        }
      }));
    }
  };

  // 일별 특근시간 변경 핸들러
  const handleOvertimeHoursChange = (date, index, value) => {
    const newOvertimeHours = {...workersData.overtimeHours};
    newOvertimeHours[date][index] = value;
    setWorkersData({...workersData, overtimeHours: newOvertimeHours});
    
    // 변경된 데이터 추적
    const attendanceId = workersData.attendanceIds[date][index];
    if (attendanceId) {
      setChangedData(prev => ({
        ...prev,
        [attendanceId]: {
          ...prev[attendanceId],
          work_hours: workersData.dailyHours[date][index],
          overtime_hours: value
        }
      }));
    }
  };

  // Excel 다운로드 핸들러 함수 추가
  const handleExcelDownload = async () => {
    try {
      setLoading(true);
      
      // 로컬 스토리지에서 회사 코드 가져오기
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const companyCode = userInfo.middleCompanyCode;
      
      if (!companyCode) {
        throw new Error('회사 코드를 찾을 수 없습니다.');
      }
      
      const blob = await downloadCompanyMonthlyExcel(
        companyCode, 
        selectedDate.year, 
        selectedDate.month
      );
      
      // 파일 다운로드 로직
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedDate.year}년_${selectedDate.month}월_근로자정보.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Excel 다운로드 오류:', error);
      alert(`Excel 다운로드 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loadingIndicator">데이터를 불러오는 중...</div>;
  if (error) return <div className="errorMessage">오류: {error}</div>;

  return (
    <div className="workerManagementPage">
      <div className="pageHeader noBorder">
        <h1 className="pageTitle">근로자 정보 관리</h1>
      </div>

      {/* 년도/달 제목을 중앙에, 버튼들은 오른쪽에 배치 */}
      <div className="monthAndButtons">
        <div className="monthNavigation">
          <button className="prevMonth" onClick={goToPrevMonth}>◄</button>
          <h2 className="currentMonth" style={{ fontSize: '30px' }}>
            {selectedDate.year}년 {selectedDate.month}월
          </h2>
          <button className="nextMonth" onClick={goToNextMonth}>►</button>
        </div>
        
        <div className="workerActionButtons">
          <button className="workerActionButton exportButton" onClick={handleExcelDownload}>
            <span className="exportIcon">↓</span> Excel로 내려받기
          </button>
          {isEditing ? (
            <button className="workerActionButton saveButton" onClick={toggleEditMode}>
              <span className="okIcon">OK</span>수정완료
            </button>
          ) : (
            <button className="workerActionButton editButton" onClick={toggleEditMode}>
              <span className="editIcon">✎</span>정보 수정하기
            </button>
          )}
        </div>
      </div>

      {/* 데이터 확인용 JSON 출력 */}
      <div className="debug-data" style={{ marginBottom: '20px', fontSize: '12px', display: 'none' }}>
        <pre>{JSON.stringify(attendanceData, null, 2)}</pre>
      </div>

      {/* 근로자 정보 테이블 */}
      {attendanceData && attendanceData.workers && attendanceData.workers.length > 0 ? (
        <div className="tableContainer">
          <table>
            <thead>
              <tr>
                <th style={{ width: '190px', minWidth: '190px', maxWidth: '190px' }}></th>
                {attendanceData.workers.map((worker, index) => (
                  <th key={worker.id || index}>
                    <a 
                      href={`/dashboard/worker-detail/${worker.id}`} 
                      className="workerNameLink" 
                      style={{color: 'black', fontWeight: 'normal'}}
                    >
                      {worker.name || `[사원${index+1}]`}
                    </a>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* 공지사항 확인 여부 */}
              <tr>
                <td className="infoLabel" style={{ width: '190px', minWidth: '190px', maxWidth: '190px' }}>공지사항 등의 여부</td>
                {attendanceData.workers.map((worker, index) => (
                  <td key={worker.id || index}>
                    {worker.notice_confirmed ? 'O' : 'X'}
                  </td>
                ))}
              </tr>
              
              {/* 시급 */}
              <tr>
                <td className="infoLabel">시급</td>
                {workersData.hourlyWages.map((wage, index) => (
                  <td key={index}>
                    {isEditing ? (
                      <input
                        type="text"
                        className="editInput"
                        value={wage}
                        onChange={(e) => handleHourlyWageChange(index, e.target.value)}
                      />
                    ) : (
                      wage
                    )}
                  </td>
                ))}
              </tr>
              
              {/* 월별 특이사항 */}
              <tr>
                <td className="infoLabel">월별 특이사항</td>
                {workersData.monthlyNotes.map((note, index) => (
                  <td key={index} className={note.trim() ? "hasContent" : ""}>
                    {isEditing ? (
                      <textarea
                        className="editInput monthlyNotes"
                        value={note}
                        onChange={(e) => handleMonthlyNotesChange(index, e.target.value)}
                        rows={note.trim() ? 4 : 2}
                      />
                    ) : (
                      note.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < note.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))
                    )}
                  </td>
                ))}
              </tr>
              
              {/* 일별 근무시간 */}
              {Object.keys(workersData.dailyHours).map((date) => (
                <tr key={date}>
                  <td className="dateLabel">{date}</td>
                  {workersData.dailyHours[date].map((hours, index) => {
                    const overtime = workersData.overtimeHours[date][index];
                    const isZeroHours = hours === '0' && overtime === '0';
                    const attendanceId = workersData.attendanceIds[date][index];
                    const isModified = workersData.modifiedRecords && workersData.modifiedRecords[date] && workersData.modifiedRecords[date][index];
                    
                    return (
                      <td 
                        key={index} 
                        className={isModified ? "modified-record" : ""}
                      >
                        {isModified && <span className="modified-indicator" title="관리자에 의해 수정됨">✎</span>}
                        
                        {isEditing ? (
                          isZeroHours || !attendanceId ? (
                            <span></span>
                          ) : (
                            <div className="editHoursContainer">
                              <div className="hoursInputRow">
                                <input
                                  type="text"
                                  className="editInput dailyHours"
                                  value={hours}
                                  onChange={(e) => handleDailyHoursChange(date, index, e.target.value)}
                                  maxLength="4"
                                />
                                <span style={{ margin: '0 2px', color: '#ff6b00' }}>(</span>
                                <input
                                  type="text"
                                  className="editInput overtimeHours"
                                  value={overtime}
                                  onChange={(e) => handleOvertimeHoursChange(date, index, e.target.value)}
                                  maxLength="3"
                                />
                                <span style={{ color: '#ff6b00' }}>)</span>
                              </div>
                            </div>
                          )
                        ) : (
                          isZeroHours ? (
                            <span></span>
                          ) : (
                            <>
                              {hours}<span className="overtimeHours">({overtime})</span>
                            </>
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              
              {/* 요약 정보 (API 데이터에서 계산) */}
              <tr>
                <td className="summaryLabel">총 근로시간</td>
                {attendanceData.workers.map((worker, index) => {
                  // 해당 직원의 총 근무시간 계산 - 안전하게 처리
                  const totalHours = (worker.total_hours !== undefined && worker.total_hours !== null) ? worker.total_hours : 0;
                  const totalOvertime = (worker.total_overtime !== undefined && worker.total_overtime !== null) ? worker.total_overtime : 0;
                  
                  return (
                    <td key={worker.id || index}>
                      {totalHours}<span className="overtimeHours">({totalOvertime})</span>
                    </td>
                  );
                })}
              </tr>
              
              {/* 규정 근무 수당 */}
              <tr>
                <td className="summaryLabel">규정근무수당</td>
                {attendanceData.workers.map((worker, index) => {
                  const regularPay = worker.regular_pay !== undefined && worker.regular_pay !== null ? 
                                     worker.regular_pay : 0;
                  return (
                    <td key={worker.id || index}>
                      {regularPay.toLocaleString()}
                    </td>
                  );
                })}
              </tr>
              
              {/* 특별 근무 수당 */}
              <tr>
                <td className="summaryLabel">특별근무수당</td>
                {attendanceData.workers.map((worker, index) => {
                  const specialPay = worker.special_pay !== undefined && worker.special_pay !== null ?
                                    worker.special_pay : 0;
                  return (
                    <td key={worker.id || index}>
                      {specialPay.toLocaleString()}
                    </td>
                  );
                })}
              </tr>
              
              {/* 주휴 수당 */}
              <tr>
                <td className="summaryLabel">주휴수당</td>
                {attendanceData.workers.map((worker, index) => {
                  const holidayPay = worker.holiday_pay !== undefined && worker.holiday_pay !== null ?
                                     worker.holiday_pay : 0;
                  return (
                    <td key={worker.id || index}>
                      {holidayPay.toLocaleString()}
                    </td>
                  );
                })}
              </tr>
              
              {/* 총 수당 */}
              <tr>
                <td className="summaryLabel">총 지급액</td>
                {attendanceData.workers.map((worker, index) => {
                  // 모든 값 안전하게 처리
                  const regularPay = worker.regular_pay !== undefined && worker.regular_pay !== null ? worker.regular_pay : 0;
                  const specialPay = worker.special_pay !== undefined && worker.special_pay !== null ? worker.special_pay : 0;
                  const holidayPay = worker.holiday_pay !== undefined && worker.holiday_pay !== null ? worker.holiday_pay : 0;
                  const socialInsurance = worker.social_insurance !== undefined && worker.social_insurance !== null ? worker.social_insurance : 0;
                  
                  // 총 지급액 계산 후 원단위 반올림 처리
                  const totalPayBeforeRound = regularPay + specialPay + holidayPay - socialInsurance;
                  const totalPay = Math.round(totalPayBeforeRound / 10) * 10; // 원단위 반올림
                  
                  return (
                    <td key={worker.id || index}>
                      {totalPay.toLocaleString()}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="noDataMessage">표시할 근로자 데이터가 없습니다.</div>
      )}
    </div>
  );
};

export default WorkerManagement; 